import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, AlertTriangle, Heart, Trophy, MessageCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DailyMessage {
  id: string;
  message: string;
  category: string;
}

interface DailyMessageBannerProps {
  workerId: string;
  children?: React.ReactNode;
}

const CATEGORY_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
  nudge: { icon: Sparkles, color: 'text-primary' },
  alert: { icon: AlertTriangle, color: 'text-warning' },
  recognition: { icon: Trophy, color: 'text-success' },
  tip: { icon: Heart, color: 'text-primary' },
  default: { icon: MessageCircle, color: 'text-muted-foreground' },
};

export function DailyMessageBanner({ workerId, children }: DailyMessageBannerProps) {
  const [message, setMessage] = useState<DailyMessage | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchDailyMessage();
  }, [workerId]);

  const fetchDailyMessage = async () => {
    try {
      // Fetch pending daily_message interventions for this worker
      const { data, error } = await supabase
        .from('interventions')
        .select('id, message, category')
        .eq('target_worker_id', workerId)
        .eq('delivery_method', 'daily_message')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setMessage(data);
        // Mark as viewed
        await supabase
          .from('interventions')
          .update({ viewed_at: new Date().toISOString() })
          .eq('id', data.id);
      }
    } catch (err) {
      console.error('Error fetching daily message:', err);
    }
  };

  const handleDismiss = async () => {
    if (message) {
      setDismissed(true);
      // Mark as completed
      await supabase
        .from('interventions')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', message.id);
    }
  };

  const config = CATEGORY_CONFIG[message?.category || 'default'] || CATEGORY_CONFIG.default;
  const Icon = config.icon;

  // If no message or dismissed, show children (fallback)
  if (!message || dismissed) {
    return <>{children}</>;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="relative"
      >
        <div className="flex items-start gap-3">
          <div className={`mt-1 ${config.color}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-semibold leading-tight text-foreground">
              {message.message}
            </h1>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-full hover:bg-muted transition-colors text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
